import { useEffect, useState } from "react";
import { useProjects } from "@/state/project";
import {
  createProject,
  inviteMember,
  uploadProjectDxf,
  listMembers,
  updateMemberRole,
  removeMember,
  type ProjectSummary,
  type ProjectMember,
} from "@/lib/projects";
import { logAudit } from "@/lib/audit";
import { eng } from "@/lib/engineApi";

const ROLE_OPTS: [string, string][] = [
  ["inspector", "Inspector"],
  ["qcmanager", "QC Manager"],
  ["auditor", "Auditor"],
  ["tdd", "TDD Reviewer"],
  ["contractor", "Contractor"],
  ["client", "Client (read-only)"],
];

/** Header project switcher + self-service onboarding + invite (Task 4). */
export default function ProjectSwitcher() {
  const { projects, currentId, loading, load, select } = useProjects();
  const [open, setOpen] = useState(false);
  const [modal, setModal] = useState<null | "new" | "team">(null);

  useEffect(() => {
    void load();
  }, [load]);

  const current = projects.find((p) => p.id === currentId);
  const isManager = current?.role === "qcmanager";

  // Project metadata for the tap-to-expand drawer (UI brief #1: the second header
  // ribbon row was collapsed into this dropdown off the project-name pill).
  const proj = eng().D.project;
  const meta: [string, string][] = [
    ["Location", proj.location],
    ["Coordinates", proj.gps],
    ["Client", proj.client],
    ["EPC", proj.epc],
    ["ITP", "ITP-BAL · Rev B"],
    ["Drawing set", proj.drawingRev],
  ];

  return (
    <div className={`proj-wrap ${open ? "open" : ""}`}>
      <button className="proj-btn" title="Project details & switch" onClick={() => setOpen((o) => !o)}>
        <span className="proj-dot" />
        <span className="proj-name">{current?.name || "Ballyroe Solar Farm"}</span>
        <span className="proj-caret">▾</span>
      </button>
      {open && (
        <div className="proj-menu">
          <div className="proj-meta">
            {meta.map(([k, v]) => (
              <div className="proj-meta-row" key={k}>
                <b>{k}</b>
                <span>{v}</span>
              </div>
            ))}
          </div>
          <div className="proj-divider" />
          <div className="proj-section">Projects{loading ? " · syncing…" : ""}</div>
          {projects.map((p) => (
            <button
              key={p.id}
              className={`proj-item ${p.id === currentId ? "sel" : ""}`}
              onClick={async () => {
                setOpen(false);
                await select(p.id);
              }}
            >
              <span className="proj-item-name">{p.name}</span>
              <span className="proj-item-meta">
                {p.client || "—"} · {p.role}
              </span>
            </button>
          ))}
          <div className="proj-divider" />
          <button className="proj-action" onClick={() => { setModal("new"); setOpen(false); }}>
            ＋ New project…
          </button>
          {isManager && (
            <button className="proj-action" onClick={() => { setModal("team"); setOpen(false); }}>
              ✉ Manage team…
            </button>
          )}
        </div>
      )}

      {modal === "new" && <NewProjectWizard onClose={() => setModal(null)} />}
      {modal === "team" && current && (
        <TeamModal project={current} onClose={() => setModal(null)} />
      )}

      <style>{PROJ_CSS}</style>
    </div>
  );
}

// ---- Onboarding wizard -----------------------------------------------------
function NewProjectWizard({ onClose }: { onClose: () => void }) {
  const { load, select } = useProjects();
  const [step, setStep] = useState(1);
  const [f, setF] = useState({ name: "", location: "", client: "", epc: "", itpId: "" });
  const [dxf, setDxf] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setF((s) => ({ ...s, [k]: e.target.value }));

  async function finish() {
    setBusy(true);
    setErr(null);
    try {
      const id = await createProject(f);
      await logAudit("project.create", "project", id, { name: f.name });
      // Capture + queue the DXF for parsing (best-effort — the project is already
      // created with a working ITP; the map populates once the parse job runs).
      if (dxf) {
        try {
          await uploadProjectDxf(id, dxf);
        } catch (ue) {
          console.warn("DXF upload failed (project still created):", ue);
        }
      }
      await load();
      await select(id);
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not create project");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title="New project" onClose={onClose}>
      <div className="wiz-steps">
        {["Details", "ITP", "Confirm"].map((s, i) => (
          <span key={s} className={`wiz-step ${step === i + 1 ? "on" : ""} ${step > i + 1 ? "done" : ""}`}>
            {i + 1}. {s}
          </span>
        ))}
      </div>

      {step === 1 && (
        <div className="wiz-body">
          <LField label="Project name">
            <input className="gp-input" value={f.name} onChange={set("name")} placeholder="Ringaskiddy Solar Farm" />
          </LField>
          <LField label="Location">
            <input className="gp-input" value={f.location} onChange={set("location")} placeholder="Co. Cork, Ireland" />
          </LField>
          <LField label="Client">
            <input className="gp-input" value={f.client} onChange={set("client")} placeholder="ILOS Projects GmbH" />
          </LField>
          <LField label="EPC">
            <input className="gp-input" value={f.epc} onChange={set("epc")} placeholder="Conecon GmbH" />
          </LField>
        </div>
      )}

      {step === 2 && (
        <div className="wiz-body">
          <LField label="ITP reference">
            <input className="gp-input" value={f.itpId} onChange={set("itpId")} placeholder="ITP-RNG · Rev A" />
          </LField>
          <LField label="CAD layout (DXF) — optional">
            <input
              className="gp-input"
              type="file"
              accept=".dxf"
              onChange={(e) => setDxf(e.target.files?.[0] ?? null)}
            />
          </LField>
          <p className="wiz-note">
            The project starts with a standard ITP checklist. Attach a DXF to capture the CAD layout —
            it's stored and queued for parsing into the table / pile map (a background job). You can
            also add it later.
          </p>
        </div>
      )}

      {step === 3 && (
        <div className="wiz-body">
          <div className="wiz-summary">
            <Row k="Project" v={f.name || "—"} />
            <Row k="Location" v={f.location || "—"} />
            <Row k="Client" v={f.client || "—"} />
            <Row k="EPC" v={f.epc || "—"} />
            <Row k="ITP" v={f.itpId || "—"} />
            <Row k="DXF" v={dxf ? dxf.name : "none (add later)"} />
          </div>
          <p className="wiz-note">You'll be added as the project's QC Manager.</p>
        </div>
      )}

      {err && <div className="wiz-err">{err}</div>}

      <div className="wiz-foot">
        {step > 1 && (
          <button className="btn" onClick={() => setStep((s) => s - 1)} disabled={busy}>
            ← Back
          </button>
        )}
        <div style={{ flex: 1 }} />
        {step < 3 ? (
          <button className="btn btn-primary" disabled={step === 1 && !f.name.trim()} onClick={() => setStep((s) => s + 1)}>
            Next →
          </button>
        ) : (
          <button className="btn btn-primary" disabled={busy || !f.name.trim()} onClick={finish}>
            {busy ? "Creating…" : "Create project →"}
          </button>
        )}
      </div>
    </Modal>
  );
}

// ---- Team modal (member list + role change + remove + invite) --------------
function TeamModal({ project, onClose }: { project: ProjectSummary; onClose: () => void }) {
  const [members, setMembers] = useState<ProjectMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("inspector");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const refresh = async () => {
    setLoading(true);
    try {
      setMembers(await listMembers(project.id));
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not load members");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.id]);

  const qcManagers = members.filter((m) => m.role === "qcmanager").length;

  async function changeRole(m: ProjectMember, newRole: string) {
    setErr(null);
    setMsg(null);
    try {
      await updateMemberRole(project.id, m.user_id, newRole);
      await refresh();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not change role");
      await refresh(); // resync the <select> back to the real role on rejection
    }
  }
  async function remove(m: ProjectMember) {
    if (!confirm(`Remove ${m.full_name || m.email} from ${project.name}?`)) return;
    setErr(null);
    setMsg(null);
    try {
      await removeMember(project.id, m.user_id);
      await refresh();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not remove member");
    }
  }
  async function send() {
    setBusy(true);
    setErr(null);
    setMsg(null);
    try {
      const r = await inviteMember(project.id, email.trim(), role);
      await logAudit("member.invite", "member", email.trim(), { role });
      setMsg(
        r.resolved
          ? `Added ${r.email} as ${r.role}.`
          : r.emailed
            ? `Invitation emailed to ${r.email} — they'll join as ${r.role} after setting a password.`
            : `Invite recorded for ${r.email}. They'll join as ${r.role} when they sign up.`,
      );
      setEmail("");
      await refresh();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not send invite");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title={`Team · ${project.name}`} onClose={onClose}>
      <div className="team-list">
        {loading ? (
          <div className="team-empty">Loading…</div>
        ) : members.length === 0 ? (
          <div className="team-empty">No members yet.</div>
        ) : (
          members.map((m) => {
            const lastQc = m.role === "qcmanager" && qcManagers <= 1;
            return (
              <div className="team-row" key={m.user_id}>
                <div className="team-id">
                  <b>{m.full_name || m.email.split("@")[0]}</b>
                  <span>{m.email}</span>
                </div>
                <select
                  className="gp-input team-role"
                  value={m.role}
                  disabled={lastQc}
                  title={lastQc ? "The last QC manager can't be changed" : "Change role"}
                  onChange={(e) => changeRole(m, e.target.value)}
                >
                  {ROLE_OPTS.map(([v, l]) => (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  ))}
                </select>
                <button
                  className="team-x"
                  disabled={lastQc}
                  title={lastQc ? "The last QC manager can't be removed" : "Remove member"}
                  onClick={() => remove(m)}
                >
                  ✕
                </button>
              </div>
            );
          })
        )}
      </div>

      <div className="team-invite">
        <div className="proj-section" style={{ padding: "10px 0 6px" }}>
          Invite a teammate
        </div>
        <div className="team-invite-row">
          <input
            className="gp-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="teammate@company.com"
          />
          <select className="gp-input team-role" value={role} onChange={(e) => setRole(e.target.value)}>
            {ROLE_OPTS.map(([v, l]) => (
              <option key={v} value={v}>
                {l}
              </option>
            ))}
          </select>
          <button className="btn btn-primary" disabled={busy || !email.trim()} onClick={send}>
            {busy ? "…" : "Invite"}
          </button>
        </div>
      </div>

      {msg && <div className="wiz-ok">{msg}</div>}
      {err && <div className="wiz-err">{err}</div>}
    </Modal>
  );
}

// ---- shared bits -----------------------------------------------------------
function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="gp-modal-overlay" onClick={onClose}>
      <div className="gp-modal" onClick={(e) => e.stopPropagation()}>
        <div className="gp-modal-head">
          <b>{title}</b>
          <button className="gp-modal-x" onClick={onClose}>
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
function LField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="lfield">
      <span>{label}</span>
      {children}
    </label>
  );
}
function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="wiz-row">
      <b>{k}</b>
      <span>{v}</span>
    </div>
  );
}

const PROJ_CSS = `
.proj-wrap{ position:relative; }
.proj-btn{ display:flex; align-items:center; gap:8px; padding:6px 10px; background:var(--panel2,#141d27);
  border:1px solid var(--line); border-radius:8px; color:var(--ink); cursor:pointer; font-size:13px; max-width:230px; }
.proj-dot{ width:8px; height:8px; border-radius:50%; background:var(--steel,#3A719E); flex:none; }
.proj-name{ font-weight:600; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.proj-caret{ color:var(--dim); font-size:10px; }
.proj-menu{ position:absolute; top:calc(100% + 6px); left:0; min-width:280px; z-index:60;
  background:var(--panel,#0e1620); border:1px solid var(--line); border-radius:10px; padding:6px;
  box-shadow:0 12px 32px rgba(0,0,0,.45); }
.proj-section{ font-size:10px; letter-spacing:.08em; text-transform:uppercase; color:var(--dim); padding:6px 8px; }
.proj-meta{ display:grid; gap:6px; padding:6px 8px 2px; }
.proj-meta-row{ display:flex; justify-content:space-between; gap:14px; font-size:12px; }
.proj-meta-row b{ color:var(--dim); font-weight:600; text-transform:uppercase; letter-spacing:.06em; font-size:9.5px; align-self:center; }
.proj-meta-row span{ color:var(--ink); font-weight:500; text-align:right; font-family:'IBM Plex Mono',monospace; font-size:11.5px; }
.proj-item{ display:flex; flex-direction:column; gap:2px; width:100%; text-align:left; padding:7px 8px;
  background:transparent; border:0; border-radius:7px; color:var(--ink); cursor:pointer; }
.proj-item:hover{ background:var(--panel2,#141d27); }
.proj-item.sel{ background:rgba(58,113,158,.16); }
.proj-item-name{ font-weight:600; font-size:13px; }
.proj-item-meta{ font-size:11px; color:var(--dim); }
.proj-divider{ height:1px; background:var(--line); margin:6px 4px; }
.proj-action{ width:100%; text-align:left; padding:8px; background:transparent; border:0; border-radius:7px;
  color:var(--steel,#3A719E); font-weight:600; font-size:13px; cursor:pointer; }
.proj-action:hover{ background:var(--panel2,#141d27); }

.gp-modal-overlay{ position:fixed; inset:0; background:rgba(4,8,12,.6); z-index:200;
  display:flex; align-items:center; justify-content:center; }
.gp-modal{ width:440px; max-width:92vw; background:var(--panel,#0e1620); border:1px solid var(--line);
  border-radius:14px; padding:18px; box-shadow:0 24px 64px rgba(0,0,0,.5); }
.gp-modal-head{ display:flex; align-items:center; justify-content:space-between; margin-bottom:14px; font-size:15px; }
.gp-modal-x{ background:transparent; border:0; color:var(--dim); cursor:pointer; font-size:16px; }
.wiz-steps{ display:flex; gap:6px; margin-bottom:14px; }
.wiz-step{ font-size:11px; color:var(--dim); padding:4px 8px; border-radius:6px; background:var(--panel2,#141d27); }
.wiz-step.on{ color:var(--ink); border:1px solid var(--steel,#3A719E); }
.wiz-step.done{ color:var(--green,#2C9B73); }
.wiz-body{ display:grid; gap:12px; }
.lfield{ display:grid; gap:6px; }
.lfield > span{ font-size:11px; text-transform:uppercase; letter-spacing:.04em; color:var(--dim); }
.gp-input{ width:100%; box-sizing:border-box; padding:9px 11px; background:var(--panel2,#141d27);
  color:var(--ink); border:1px solid var(--line); border-radius:8px; font:14px/1.2 'IBM Plex Sans',system-ui,sans-serif; }
.gp-input:focus{ outline:none; border-color:var(--steel,#3A719E); }
.wiz-note{ font-size:12px; color:var(--dim); line-height:1.5; margin:0; }
.wiz-summary{ display:grid; gap:8px; }
.wiz-row{ display:flex; justify-content:space-between; font-size:13px; border-bottom:1px solid var(--line); padding-bottom:6px; }
.wiz-row b{ color:var(--dim); font-weight:600; }
.wiz-foot{ display:flex; align-items:center; gap:8px; margin-top:18px; }
.wiz-err{ color:var(--rust,#C0563B); font-size:13px; margin-top:10px; }
.wiz-ok{ color:var(--green,#2C9B73); font-size:13px; margin-top:10px; }

.team-list{ display:grid; gap:6px; max-height:300px; overflow:auto; }
.team-empty{ padding:18px; text-align:center; color:var(--dim); font-size:13px; }
.team-row{ display:flex; align-items:center; gap:8px; padding:7px 8px; border:1px solid var(--line);
  border-radius:8px; background:var(--panel2,#141d27); }
.team-id{ display:flex; flex-direction:column; gap:1px; flex:1; min-width:0; }
.team-id b{ font-size:13px; }
.team-id span{ font-size:11px; color:var(--dim); overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.team-role{ width:auto; padding:5px 8px; font-size:12px; }
.team-x{ background:transparent; border:1px solid var(--line); color:var(--dim); width:28px; height:28px;
  border-radius:6px; cursor:pointer; flex:none; }
.team-x:hover:not(:disabled){ color:var(--rust,#C0563B); border-color:var(--rust,#C0563B); }
.team-x:disabled,.team-role:disabled{ opacity:.45; cursor:not-allowed; }
.team-invite{ margin-top:14px; border-top:1px solid var(--line); }
.team-invite-row{ display:flex; gap:8px; }
.team-invite-row .gp-input[type=email]{ flex:1; }
`;
